// 性能监控工具
export class PerformanceMonitor {
  static metrics = new Map()
  static observers = new Map()

  /**
   * 开始性能测量
   * @param {string} name - 测量名称
   * @returns {Object} 测量对象
   */
  static startMeasure(name) {
    const startTime = performance.now()
    const measure = {
      name,
      startTime,
      endTime: null,
      duration: null,
      metadata: {}
    }
    
    this.metrics.set(name, measure)
    return measure
  }

  /**
   * 结束性能测量
   * @param {string} name - 测量名称
   * @param {Object} metadata - 额外元数据
   * @returns {Object} 测量结果
   */
  static endMeasure(name, metadata = {}) {
    const measure = this.metrics.get(name)
    if (!measure) {
      console.warn(`性能测量 "${name}" 未找到`)
      return null
    }

    const endTime = performance.now()
    measure.endTime = endTime
    measure.duration = endTime - measure.startTime
    measure.metadata = { ...measure.metadata, ...metadata }

    // 记录到控制台（开发环境）
    if (import.meta.env.DEV) {
      console.log(`⏱️ 性能测量 [${name}]: ${measure.duration.toFixed(2)}ms`, measure.metadata)
    }

    return measure
  }

  /**
   * 测量异步函数执行时间
   * @param {Function} fn - 异步函数
   * @param {string} name - 测量名称
   * @param {Object} metadata - 额外元数据
   * @returns {Promise} 函数执行结果
   */
  static async measureAsync(fn, name, metadata = {}) {
    this.startMeasure(name)
    try {
      const result = await fn()
      this.endMeasure(name, { ...metadata, success: true })
      return result
    } catch (error) {
      this.endMeasure(name, { ...metadata, success: false, error: error.message })
      throw error
    }
  }

  /**
   * 测量同步函数执行时间
   * @param {Function} fn - 同步函数
   * @param {string} name - 测量名称
   * @param {Object} metadata - 额外元数据
   * @returns {*} 函数执行结果
   */
  static measureSync(fn, name, metadata = {}) {
    this.startMeasure(name)
    try {
      const result = fn()
      this.endMeasure(name, { ...metadata, success: true })
      return result
    } catch (error) {
      this.endMeasure(name, { ...metadata, success: false, error: error.message })
      throw error
    }
  }

  /**
   * 监控页面加载性能
   */
  static monitorPageLoad() {
    if (typeof window === 'undefined') return

    window.addEventListener('load', () => {
      const navigation = performance.getEntriesByType('navigation')[0]
      if (navigation) {
        const loadMetrics = {
          dns: navigation.domainLookupEnd - navigation.domainLookupStart,
          tcp: navigation.connectEnd - navigation.connectStart,
          request: navigation.responseStart - navigation.requestStart,
          response: navigation.responseEnd - navigation.responseStart,
          dom: navigation.domContentLoadedEventEnd - navigation.domContentLoadedEventStart,
          load: navigation.loadEventEnd - navigation.loadEventStart,
          total: navigation.loadEventEnd - navigation.navigationStart
        }

        this.metrics.set('pageLoad', {
          name: 'pageLoad',
          duration: loadMetrics.total,
          metadata: loadMetrics
        })

        if (import.meta.env.DEV) {
          console.log('📊 页面加载性能:', loadMetrics)
        }
      }
    })
  }

  /**
   * 监控资源加载性能
   */
  static monitorResourceLoad() {
    if (typeof window === 'undefined') return

    const observer = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        if (entry.entryType === 'resource') {
          const resourceMetrics = {
            name: entry.name,
            duration: entry.duration,
            size: entry.transferSize || 0,
            type: entry.initiatorType
          }

          this.metrics.set(`resource_${entry.name}`, {
            name: `resource_${entry.name}`,
            duration: entry.duration,
            metadata: resourceMetrics
          })
        }
      }
    })

    observer.observe({ entryTypes: ['resource'] })
    this.observers.set('resource', observer)
  }

  /**
   * 监控长任务
   */
  static monitorLongTasks() {
    if (typeof window === 'undefined') return

    const observer = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        if (entry.duration > 50) { // 超过50ms的任务
          const longTaskMetrics = {
            duration: entry.duration,
            startTime: entry.startTime,
            name: entry.name
          }

          this.metrics.set(`longTask_${entry.startTime}`, {
            name: `longTask_${entry.startTime}`,
            duration: entry.duration,
            metadata: longTaskMetrics
          })

          if (import.meta.env.DEV) {
            console.warn('⚠️ 检测到长任务:', longTaskMetrics)
          }
        }
      }
    })

    observer.observe({ entryTypes: ['longtask'] })
    this.observers.set('longtask', observer)
  }

  /**
   * 监控内存使用情况
   */
  static monitorMemory() {
    if (typeof window === 'undefined' || !window.performance.memory) return

    const memoryInfo = window.performance.memory
    const memoryMetrics = {
      used: memoryInfo.usedJSHeapSize,
      total: memoryInfo.totalJSHeapSize,
      limit: memoryInfo.jsHeapSizeLimit,
      usage: (memoryInfo.usedJSHeapSize / memoryInfo.jsHeapSizeLimit) * 100
    }

    this.metrics.set('memory', {
      name: 'memory',
      duration: 0,
      metadata: memoryMetrics
    })

    if (import.meta.env.DEV) {
      console.log('🧠 内存使用情况:', memoryMetrics)
    }

    return memoryMetrics
  }

  /**
   * 获取性能报告
   * @returns {Object} 性能报告
   */
  static getReport() {
    const measures = Array.from(this.metrics.values())
    const totalMeasures = measures.length
    const avgDuration = measures.reduce((sum, m) => sum + (m.duration || 0), 0) / totalMeasures
    const slowestMeasure = measures.reduce((max, m) => 
      (m.duration || 0) > (max.duration || 0) ? m : max, { duration: 0 }
    )

    return {
      totalMeasures,
      avgDuration: avgDuration.toFixed(2),
      slowestMeasure,
      measures: measures.sort((a, b) => (b.duration || 0) - (a.duration || 0)),
      timestamp: new Date().toISOString()
    }
  }

  /**
   * 清理性能监控
   */
  static cleanup() {
    this.observers.forEach(observer => observer.disconnect())
    this.observers.clear()
    this.metrics.clear()
  }

  /**
   * 初始化性能监控
   */
  static init() {
    this.monitorPageLoad()
    this.monitorResourceLoad()
    this.monitorLongTasks()
    
    // 定期监控内存使用
    setInterval(() => {
      this.monitorMemory()
    }, 30000) // 每30秒检查一次
  }
}

// 自动初始化
if (typeof window !== 'undefined') {
  PerformanceMonitor.init()
}

export default PerformanceMonitor

